import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { trackMetaEvent } from "@/lib/metaPixel";
import AppErrorBoundary from "@/components/analytics/AppErrorBoundary";
import PostHogRouteTracker from "@/components/analytics/PostHogRouteTracker";
import PostHogReplayGuard from "@/components/analytics/PostHogReplayGuard";
import CanonicalTag from "@/components/seo/CanonicalTag";


import AccessGate from "@/components/access/AccessGate";
import { SfxProvider } from "@/providers/SfxProvider";
import { MusicProvider } from "@/providers/MusicProvider";
import { InstructorVoiceProvider } from "@/providers/InstructorVoiceProvider";
import InstructorVoiceCaption from "@/components/launch/InstructorVoiceCaption";
import { LessonVoiceProvider } from "@/providers/LessonVoiceProvider";
import LessonVoiceMomentCaption from "@/components/lesson/LessonVoiceMomentCaption";
import RoleGuard from "@/components/auth/RoleGuard";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import CourseLessons from "./pages/CourseLessons";
import LessonScreen from "./pages/LessonScreen";
import AdminUpload from "./pages/AdminUpload";
import MCQProcessor from "./pages/MCQProcessor";
import FinancialDashboard from "./pages/FinancialDashboard";
import InvestingSimulator from "./pages/InvestingSimulator";
import HomeBuyingSimulator from "./pages/HomeBuyingSimulator";
import REInvestingSimulator from "./pages/REInvestingSimulator";
import EntrepreneurshipSimulator from "./pages/EntrepreneurshipSimulator";
import VehicleOwnershipSimulator from "./pages/VehicleOwnershipSimulator";
import AccountSetupModule from "./pages/AccountSetupModule";
import CurriculumMap from "./pages/CurriculumMap";
import AchievementsScreen from "./pages/AchievementsScreen";
import ProfileScreen from "./pages/ProfileScreen";
import FriendsScreen from "./pages/FriendsScreen";
import BudgetBuilderModule from "./pages/BudgetBuilderModule";
import CreditManagementModule from "./pages/CreditManagementModule";
import CareerPathModule from "./pages/CareerPathModule";
import RiskProtectionModule from "./pages/RiskProtectionModule";
import FamilyBudgetModule from "./pages/FamilyBudgetModule";
import TaxStrategyModule from "./pages/TaxStrategyModule";
import WealthStrategyModule from "./pages/WealthStrategyModule";
import DevelopmentSimulator from "./pages/DevelopmentSimulator";
import RecoveryModeModule from "./pages/RecoveryModeModule";
import ScamDefenseModule from "./pages/ScamDefenseModule";
import ParentDashboard from "./pages/ParentDashboard";
import FinancialVocabulary from "./pages/FinancialVocabulary";
import SupportCenter from "./pages/SupportCenter";
import FeedbackCenter from "./pages/FeedbackCenter";
import AdminReviewDashboard from "./pages/AdminReviewDashboard";
import FounderReadinessDashboard from "./pages/FounderReadinessDashboard";
import LaunchQAReview from "./pages/LaunchQAReview";
import AccountSecurity from "./pages/AccountSecurity";
import AccountBilling from "./pages/AccountBilling";
import { CutoverGate } from "@/components/learners/CutoverGate";
import NotFound from "./pages/NotFound";
import AssessmentPage from "./pages/AssessmentPage";
import AIContentStudio from "./pages/AIContentStudio";
import AdminIntegrations from "./pages/AdminIntegrations";
import AdminUnitEconomics from "./pages/AdminUnitEconomics";
import Unsubscribe from "./pages/Unsubscribe";
import OAuthConsent from "./pages/OAuthConsent";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ParentPrivacy from "./pages/ParentPrivacy";
import AdminPrivacyCompliance from "./pages/AdminPrivacyCompliance";
import AdminConceptTagReview from "./pages/AdminConceptTagReview";
import AdminCurriculumCertification from "./pages/AdminCurriculumCertification";
import AdminLaunchQuestions from "./pages/AdminLaunchQuestions";
import AdminLearningEngine from "./pages/AdminLearningEngine";
import AdminContentGeneration from "./pages/AdminContentGeneration";
import ChildrenPrivacyNotice from "./pages/ChildrenPrivacyNotice";
import ParentalConsent from "./pages/ParentalConsent";
import DataDeletionRequest from "./pages/DataDeletionRequest";
import EconomicsModule from "./pages/EconomicsModule";
import ChallengeDuel from "./pages/ChallengeDuel";
import ChallengeCenter from "./pages/ChallengeCenter";
import GuidesPage from "./pages/GuidesPage";
import ResourcesPage from "./pages/ResourcesPage";
import CreateUsername from "./pages/CreateUsername";
import Pricing from "./pages/Pricing";
import AdminDashboardDataHealth from "./pages/AdminDashboardDataHealth";
import AdminQaTestHousehold from "./pages/AdminQaTestHousehold";
import AdminInstructorVoice from "./pages/AdminInstructorVoice";
import AdminLessonCaptions from "./pages/AdminLessonCaptions";
import LaunchModuleGate from "./components/launch/LaunchModuleGate";
import FloatingAudioToggle from "@/components/launch/FloatingAudioToggle";

const queryClient = new QueryClient();

function ProtectedRoute({ children, bypassGate }: { children: React.ReactNode; bypassGate?: boolean }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background"><p className="text-muted-foreground">Loading...</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Routing rule: social identity fields (username/avatar/display
  // name) are optional and must never block Home/Dashboard/app routes. The
  // only hard app gate after auth is profiles.onboarding_completed, handled by
  // Dashboard/parent setup flows.
  if (bypassGate) return <div className="theme-auth">{children}</div>;
  return <div className="theme-auth"><AccessGate>{children}</AccessGate></div>;
}

/** Auth-only wrapper for the username setup page itself (no subscription/username gates). */
function UsernameSetupRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background"><p className="text-muted-foreground">Loading...</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <div className="theme-auth">{children}</div>;
}

/** Wraps content with the original light theme for Landing & Login pages */
const LightTheme = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => <div ref={ref} className="theme-light">{children}</div>
);
LightTheme.displayName = "LightTheme";

/** Forces LessonScreen to remount when lessonId changes */
const LessonScreenWrapper = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  return <LessonScreen key={lessonId} />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Landing />} />
    <Route path="/.lovable/oauth/consent" element={<LightTheme><OAuthConsent /></LightTheme>} />
    <Route path="/index" element={<Navigate to="/" replace />} />
    <Route path="/get-started" element={<Navigate to="/" replace />} />
    <Route path="/auth" element={<Navigate to="/login" replace />} />
    <Route path="/login" element={<Login />} />
    <Route path="/forgot-password" element={<LightTheme><ForgotPassword /></LightTheme>} />
    <Route path="/reset-password" element={<LightTheme><ResetPassword /></LightTheme>} />
    <Route path="/create-username" element={<UsernameSetupRoute><CreateUsername /></UsernameSetupRoute>} />
    <Route path="/pricing" element={<Pricing />} />
    {/* Retired beta entry points — permanently redirected to paid pricing. */}
    <Route path="/beta" element={<Navigate to="/pricing" replace />} />
    <Route path="/beta/*" element={<Navigate to="/pricing" replace />} />
    <Route path="/admin/dashboard-data-health" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminDashboardDataHealth /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/qa-test-household" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminQaTestHousehold /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/audio/instructor-voice" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminInstructorVoice /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/content/captions" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminLessonCaptions /></RoleGuard></ProtectedRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    <Route path="/course" element={<Navigate to="/dashboard" replace />} />
    <Route path="/course/:courseName" element={<ProtectedRoute><CourseLessons /></ProtectedRoute>} />
    <Route path="/course/*" element={<Navigate to="/dashboard" replace />} />
    <Route path="/courses" element={<Navigate to="/dashboard" replace />} />
    <Route path="/lesson/:lessonId" element={<ProtectedRoute><LessonScreenWrapper /></ProtectedRoute>} />
    <Route path="/assessment/:assessmentId" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
    <Route path="/financial-vocabulary" element={<ProtectedRoute><FinancialVocabulary /></ProtectedRoute>} />
    <Route path="/vocabulary" element={<Navigate to="/financial-vocabulary" replace />} />
    <Route path="/guides" element={<ProtectedRoute bypassGate><GuidesPage /></ProtectedRoute>} />
    <Route path="/resources" element={<ProtectedRoute bypassGate><ResourcesPage /></ProtectedRoute>} />
    <Route path="/learn" element={<Navigate to="/dashboard" replace />} />
    <Route path="/finances" element={<ProtectedRoute><FinancialDashboard /></ProtectedRoute>} />
    <Route path="/support" element={<ProtectedRoute bypassGate><SupportCenter /></ProtectedRoute>} />
    <Route path="/feedback" element={<ProtectedRoute bypassGate><FeedbackCenter /></ProtectedRoute>} />
    <Route path="/settings/security" element={<ProtectedRoute><AccountSecurity /></ProtectedRoute>} />
    <Route path="/settings/billing" element={<ProtectedRoute bypassGate><CutoverGate fallback={<NotFound />}><AccountBilling /></CutoverGate></ProtectedRoute>} />
    
    <Route path="/simulator" element={<ProtectedRoute><InvestingSimulator /></ProtectedRoute>} />
    <Route path="/homebuying" element={<ProtectedRoute><HomeBuyingSimulator /></ProtectedRoute>} />
    <Route path="/re-investing" element={<ProtectedRoute><REInvestingSimulator /></ProtectedRoute>} />
    <Route path="/entrepreneurship" element={<ProtectedRoute><EntrepreneurshipSimulator /></ProtectedRoute>} />
    <Route path="/vehicle-ownership" element={<ProtectedRoute><VehicleOwnershipSimulator /></ProtectedRoute>} />
    <Route path="/account-setup" element={<ProtectedRoute><AccountSetupModule /></ProtectedRoute>} />
    <Route path="/curriculum" element={<ProtectedRoute><CurriculumMap /></ProtectedRoute>} />
    <Route path="/curriculum/*" element={<ProtectedRoute><Navigate to="/curriculum" replace /></ProtectedRoute>} />
    <Route path="/budget-builder" element={<ProtectedRoute><LaunchModuleGate moduleKey="budget_builder"><BudgetBuilderModule /></LaunchModuleGate></ProtectedRoute>} />
    <Route path="/credit-management" element={<ProtectedRoute><LaunchModuleGate moduleKey="credit_management"><CreditManagementModule /></LaunchModuleGate></ProtectedRoute>} />
    <Route path="/career-path" element={<ProtectedRoute><CareerPathModule /></ProtectedRoute>} />
    <Route path="/risk-protection" element={<ProtectedRoute><RiskProtectionModule /></ProtectedRoute>} />
    <Route path="/family-budget" element={<ProtectedRoute><FamilyBudgetModule /></ProtectedRoute>} />
    <Route path="/tax-strategy" element={<ProtectedRoute><TaxStrategyModule /></ProtectedRoute>} />
    <Route path="/wealth-strategy" element={<ProtectedRoute><WealthStrategyModule /></ProtectedRoute>} />
    <Route path="/development" element={<ProtectedRoute><DevelopmentSimulator /></ProtectedRoute>} />
    <Route path="/recovery-mode" element={<ProtectedRoute><RecoveryModeModule /></ProtectedRoute>} />
     <Route path="/scam-defense" element={<ProtectedRoute><LaunchModuleGate moduleKey="scam_defense"><ScamDefenseModule /></LaunchModuleGate></ProtectedRoute>} />
     <Route path="/econ/:slug" element={<ProtectedRoute><EconomicsModule /></ProtectedRoute>} />
    <Route path="/missions" element={<Navigate to="/achievements" replace />} />
    <Route path="/achievements" element={<ProtectedRoute><AchievementsScreen /></ProtectedRoute>} />
    <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
    <Route path="/friends" element={<ProtectedRoute><FriendsScreen /></ProtectedRoute>} />
    <Route path="/challenge/:challengeId" element={<ProtectedRoute><ChallengeDuel /></ProtectedRoute>} />
    <Route path="/challenges" element={<ProtectedRoute><ChallengeCenter /></ProtectedRoute>} />
    <Route path="/parent-dashboard" element={<ProtectedRoute bypassGate><RoleGuard kind="block_children"><ParentDashboard /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/upload" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminUpload /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/mcq-processor" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><MCQProcessor /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/ai-studio" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AIContentStudio /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/review" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminReviewDashboard /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/founder-readiness" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><FounderReadinessDashboard /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/launch-qa" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><LaunchQAReview /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/integrations" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminIntegrations /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/unit-economics" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminUnitEconomics /></RoleGuard></ProtectedRoute>} />
     <Route path="/privacy-policy" element={<PrivacyPolicy />} />
     <Route path="/terms-of-service" element={<TermsOfService />} />
     <Route path="/unsubscribe" element={<Unsubscribe />} />
     <Route path="/children-privacy-notice" element={<ChildrenPrivacyNotice />} />
     <Route path="/parental-consent" element={<ParentalConsent />} />
     <Route path="/data-deletion-request" element={<DataDeletionRequest />} />
     <Route path="/parent/privacy" element={<ProtectedRoute bypassGate><RoleGuard kind="block_children"><ParentPrivacy /></RoleGuard></ProtectedRoute>} />
     <Route path="/admin/privacy-compliance" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminPrivacyCompliance /></RoleGuard></ProtectedRoute>} />
     <Route path="/admin/concept-tag-review" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminConceptTagReview /></RoleGuard></ProtectedRoute>} />
     <Route path="/admin/curriculum-certification" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminCurriculumCertification /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/launch-questions" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminLaunchQuestions /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/learning-engine" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminLearningEngine /></RoleGuard></ProtectedRoute>} />
    <Route path="/admin/content-generation" element={<ProtectedRoute bypassGate><RoleGuard kind="admin_only"><AdminContentGeneration /></RoleGuard></ProtectedRoute>} />
     <Route path="*" element={<NotFound />} />
  </Routes>
);

// Fire Meta Pixel PageView on every SPA route change
function MetaPixelRouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    // Fires both browser Pixel + server-side Conversions API (deduped via event_id)
    trackMetaEvent("PageView");
  }, [location.pathname, location.search]);
  return null;
}

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <CanonicalTag />
          <MetaPixelRouteTracker />

          <PostHogRouteTracker />
          <PostHogReplayGuard />
          
          <SfxProvider>
            <MusicProvider>
              <InstructorVoiceProvider>
                <LessonVoiceProvider>
                  <main>
                    <AppRoutes />
                  </main>
                  <FloatingAudioToggle />
                  <InstructorVoiceCaption />
                  <LessonVoiceMomentCaption />
                </LessonVoiceProvider>
              </InstructorVoiceProvider>
            </MusicProvider>
          </SfxProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
